import { createApp } from 'vue';
import Admin from './admin.vue';

import VuePatternfly from '@vue-patternfly/core';
import VuePatternflyTable from '@vue-patternfly/table';

import * as Sentry from '@sentry/vue';
import * as commonSentry from '@common/sentry';

import i18n, { $t } from './i18n';
import { createPinia } from 'pinia';
import appGlobals from './app-globals';
import { useLoginStore } from './store/login';

import router from './router';
import { setDefaultFixedTimezone } from '@common/date';
import axios from 'axios';
import { installChunkReload, isReloadingForUpdate } from '@common/chunk-reload';

setDefaultFixedTimezone();

const app = createApp(Admin);
app.use(createPinia());

Sentry.init({
  ...commonSentry,
  app,
  dsn: typeof import.meta.env.SENTRY_DSN === 'string' ? import.meta.env.SENTRY_DSN : undefined,
  release: typeof import.meta.env.SENTRY_RELEASE === 'string' ? import.meta.env.SENTRY_RELEASE : undefined,
  environment: !window.location.hostname.endsWith('.net') ? 'development' :
    window.location.hostname.includes('-staging.') ? 'staging' : 'production',
  allowUrls: [/https:\/\/admin(?:-staging)?\.artera\.net/, /https?:\/\/admin\.artera\.vm/],
  integrations: [
    Sentry.browserTracingIntegration({ router }),
  ],
  tracesSampleRate: 1.0,
  tracePropagationTargets: [/^https:\/\/admin(?:-staging)?\.artera\.net\//, /^https:\/\/admin\.artera\.vm\//, /^\//],
  beforeSend(event, hint) {
    // a newer version was deployed and the page is reloading to fetch it: any error raised meanwhile is a consequence
    // (e.g. vue-router's "Couldn't resolve component" when a swallowed vite:preloadError resolves the import to undefined)
    if (isReloadingForUpdate()) {
      return null;
    }

    // Check if it is an exception, and if so, show the report dialog
    if (event.exception && event.event_id) {
      if (axios.isAxiosError(hint.originalException)) {
        if (!event.message && typeof hint.originalException.response?.data === 'object' && hint.originalException.response.data.message) {
          event.message = hint.originalException.response.data.message;
        }

        if (hint.originalException.status && hint.originalException.status >= 400 && hint.originalException.status < 500) {
          return event;
        }
      }

      const user: Required<NonNullable<Parameters<typeof Sentry.showReportDialog>[0]>>['user'] = {};
      try {
        const login = useLoginStore();
        user.email = login.username ?? undefined;
        user.name = login.azienda;
      } catch {
        // ignore
      }

      Sentry.showReportDialog({
        eventId: event.event_id,
        user,
        title: $t('Si è verificato un errore inaspettato'),
        subtitle: $t('Il reparto è stato notificato.'),
        subtitle2: $t('Per aiutare a identificare il problema descrivi cosa è successo qui sotto.'),
        labelName: $t('Operatore'),
        labelComments: $t('Cosa è successo?'),
        labelClose: $t('Chiudi'),
        labelSubmit: $t('Invia report'),
        successMessage: $t('Feedback inviato. Grazie!'),
      });
    }
    return event;
  },

});

app.use(VuePatternfly);
app.use(VuePatternflyTable);
installChunkReload(router);
app.use(router);
app.use(i18n);
app.use(appGlobals);
app.mount('#app');
